// Central definition of asset expense category hierarchy.
// The backend returns a flat list of [value, label] tuples (ASSET_EXPENSE_CATEGORIES).
// This file defines the parent groupings and builds a proper tree for dropdowns/display.

// Master list of Automotive expense category values.
// AUTOMOTIVE_CATEGORY_VALUES: unambiguously automotive — used for domain detection.
// AUTOMOTIVE_ALL_VALUES: full set shown in the Automotive modal dropdown (includes shared values).
export const AUTOMOTIVE_CATEGORY_VALUES = [
  'asset_input',              // Fuel / Charging (old slug)
  'automotive_fuel_charging', // Fuel / Charging (new slug)
  'vehicle_maintenance',
  'automotive_maintenance',
  'vehicle_repair',
  'automotive_repair',
  'vehicle_enhancement',
  'automotive_enhancement',
  'cleaning',
  'automotive_car_wash',
  'asset_storage',            // Parking / Garage (old slug)
  'automotive_parking',       // Parking (new slug)
  'automotive_tolls',
  'licensing_tags',
  'vehicle_licensing_tags',
  'ticket_or_fine',
  'vehicle_other',
  'automotive_other',
  'initial_purchase',
  'automotive',               // Generic automotive parent value
];

// Full set for the Automotive modal category dropdown (includes context-dependent shared values).
export const AUTOMOTIVE_ALL_VALUES = [
  ...AUTOMOTIVE_CATEGORY_VALUES,
  'taxes',              // Registration / Tax Payment
  'insurance',          // Insurance Payment
  'financing_interest', // Financing Interest
  'financing_principal',// Financing Principal
];

// Master list of Residential Property expense category values.
// Any transaction with one of these categories should trigger the Residential Property link modal.
// Covers: Home (all children), Landscaping, Utilities (all children),
// Waste Management, and payment types (Debt, Interest, Principal, Tax, Insurance).
export const RESI_PROPERTY_CATEGORY_VALUES = [
  // Home
  'maintenance',
  'repair',
  'enhancement',
  'pest_control',
  'home_decor',
  'home_furniture',
  'home_artwork',
  'home_inspection',
  'home_other',
  'home_tv',
  'home_internet',
  // Landscaping
  'landscaping',
  'plants',
  // Utilities
  'utilities',
  'utilities_electric_gas',
  'utilities_water',
  'utilities_trash',
  'utilities_sewer',
  'utilities_internet',
  'utilities_cable_tv',
  'utilities_phone',
  'utilities_other',
  // Waste Management
  'waste_management',
  'sanitation',
  'waste_management__recycling',
  'waste_management__yard_waste',
  'waste_management__other',
  // Payments
  'payments',           // Debt Payment
  'financing_interest', // Interest Payment
  'financing_principal',// Principal Payment
  'taxes',              // Tax Payment
  'insurance',          // Insurance Payment
];

// Parent group definitions: each parent has a label and a list of child values.
// Children not listed here become standalone top-level items.
const CATEGORY_PARENT_GROUPS = [
  {
    value: 'automotive',
    label: 'Automotive',
    childValues: AUTOMOTIVE_ALL_VALUES,
  },
  {
    value: 'home',
    label: 'Home',
    childValues: [
      'maintenance',
      'repair',
      'enhancement',
      'pest_control',
      'home_decor',
      'home_furniture',
      'home_artwork',
      'home_inspection',
      'home_other',
      'home_tv',
      'home_internet',
    ],
  },
  {
    value: 'landscaping_group',
    label: 'Landscaping',
    childValues: ['plants', 'landscaping'],
  },
  {
    value: 'media',
    label: 'Media & Internet',
    childValues: [
      
      
      'media_phone',
      'media_streaming',
      'media_books',
      'media_other',
    ],
  },
  {
    value: 'utilities_group',
    label: 'Utilities',
    childValues: [
      'utilities',
      'utilities_electric_gas',
      'utilities_water',
      'utilities_trash',
      'utilities_sewer',
      'utilities_internet',
      'utilities_cable_tv',
      'utilities_phone',
      'utilities_other',
    ],
  },
  {
    value: 'financing',
    label: 'Financing',
    childValues: [
      'financing_interest',
      'financing_inter',
      'financing_principal',
      'payments',
    ],
  },
  {
    value: 'taxes_insurance',
    label: 'Taxes & Insurance',
    childValues: ['taxes', 'insurance'],
  },
];

// Build a lookup: childValue -> { parentValue, parentLabel }
const CHILD_TO_PARENT = {};
for (const pg of CATEGORY_PARENT_GROUPS) {
  for (const cv of pg.childValues) {
    CHILD_TO_PARENT[cv] = { parentValue: pg.value, parentLabel: pg.label };
  }
}

/**
 * Given a category value and a flat label map (value->label),
 * returns the display string "Parent Label - Child Label" or just "Label".
 */
function getCategoryDisplayLabel(value, flatLabelMap) {
  if (!value || value === 'none') return 'None';
  const parentInfo = CHILD_TO_PARENT[value];
  const childLabel = flatLabelMap?.[value] || value;
  if (parentInfo) {
    return `${parentInfo.parentLabel} - ${childLabel}`;
  }
  // Check if it's a parent group itself
  const pg = CATEGORY_PARENT_GROUPS.find(p => p.value === value);
  if (pg) return pg.label;
  return childLabel;
}

/**
 * Build a category tree from the backend list.
 * Input: array of objects with optional nested `children` arrays (from revolvers API),
 *        OR array of [value, label] tuples (from asset expense categories API).
 * Output: { items: [{value, label, children: [{value, label}]}], noneItem, flatMap }
 */
function buildCategoryTreeSorted(raw) {
  try {
    const items = Array.isArray(raw) ? raw : [];

    const byLabelAsc = (a, b) => {
      const la = String(a.label || '').toLowerCase();
      const lb = String(b.label || '').toLowerCase();
      if (la < lb) return -1; if (la > lb) return 1; return 0;
    };

    // Detect format: if first non-null item is an array, it's [value, label] tuples
    const firstItem = items.find(x => x != null);
    const isTupleFormat = Array.isArray(firstItem);

    if (isTupleFormat) {
      // Flat [value, label] tuple format — build hierarchy from CATEGORY_PARENT_GROUPS
      const flatMap = {};
      for (const it of items) {
        if (Array.isArray(it) && it.length >= 2) {
          flatMap[String(it[0])] = String(it[1]);
        }
      }
      const allChildValues = new Set();
      for (const pg of CATEGORY_PARENT_GROUPS) {
        for (const cv of pg.childValues) allChildValues.add(cv);
      }
      const parentItems = [];
      for (const pg of CATEGORY_PARENT_GROUPS) {
        const children = pg.childValues
          .filter(cv => flatMap[cv] != null)
          .map(cv => ({ value: cv, label: flatMap[cv] }))
          .sort(byLabelAsc);
        if (children.length > 0) {
          parentItems.push({ value: pg.value, label: pg.label, children });
        }
      }
      parentItems.sort(byLabelAsc);
      const virtualParentValues = new Set(CATEGORY_PARENT_GROUPS.map(pg => pg.value));
      const standalones = [];
      for (const [val, lbl] of Object.entries(flatMap)) {
        if (!allChildValues.has(val) && !virtualParentValues.has(val) && val !== 'none') {
          standalones.push({ value: val, label: lbl, children: [] });
        }
      }
      standalones.sort(byLabelAsc);
      const noneLabel = flatMap['none'] || 'None';
      return { items: [...parentItems, ...standalones], noneItem: { value: 'none', label: noneLabel }, flatMap };
    }

    // Object format (revolvers API) — already has nested children arrays
    const flatMap = {};
    const childValueSet = new Set();
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      flatMap[String(it.value)] = String(it.label || it.value);
      if (Array.isArray(it.children)) {
        for (const ch of it.children) {
          if (ch && ch.value != null) {
            childValueSet.add(String(ch.value));
            flatMap[String(ch.value)] = String(ch.label || ch.value);
          }
        }
      }
    }

    const parents = [];
    const standalones = [];
    let noneItem = { value: 'none', label: 'None' };

    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      if (String(it.value) === 'none') { noneItem = { value: 'none', label: it.label || 'None' }; continue; }
      if (childValueSet.has(String(it.value))) continue; // skip items that appear as children
      if (Array.isArray(it.children) && it.children.length > 0) {
        const children = [...it.children]
          .filter(ch => ch && ch.value != null)
          .map(ch => ({ value: String(ch.value), label: String(ch.label || ch.value), color: ch.color, bg: ch.bg }))
          .sort(byLabelAsc);
        parents.push({ value: String(it.value), label: String(it.label || it.value), children, color: it.color, bg: it.bg });
      } else {
        standalones.push({ value: String(it.value), label: String(it.label || it.value), children: [], color: it.color, bg: it.bg });
      }
    }
    parents.sort(byLabelAsc);
    standalones.sort(byLabelAsc);

    return { items: [...parents, ...standalones], noneItem, flatMap };
  } catch (e) {
    return { items: [], noneItem: { value: 'none', label: 'None' }, flatMap: {} };
  }
}

/**
 * Build an indented flat list for simple <select> dropdowns.
 * Input: same as buildCategoryTreeSorted.
 * Output: [{ value, label, indentLevel, displayLabel }]
 */
function buildIndentedCategoryList(raw) {
  try {
    const { items, noneItem } = buildCategoryTreeSorted(raw);
    const out = [];
    for (const p of items) {
      if (p.children && p.children.length > 0) {
        out.push({ value: p.value, label: p.label, indentLevel: 0, displayLabel: p.label });
        for (const ch of p.children) {
          out.push({ value: ch.value, label: ch.label, indentLevel: 1, displayLabel: `  └ ${ch.label}` });
        }
      } else {
        out.push({ value: p.value, label: p.label, indentLevel: 0, displayLabel: p.label });
      }
    }
    const hasNone = out.some(x => x.value === 'none');
    if (!hasNone) {
      out.unshift({ value: 'none', label: noneItem.label, indentLevel: 0, displayLabel: noneItem.label });
    }
    // de-dup
    const seen = new Set();
    return out.filter(row => { if (seen.has(row.value)) return false; seen.add(row.value); return true; });
  } catch (e) {
    return [{ value: 'none', label: 'Uncategorized', indentLevel: 0, displayLabel: 'Uncategorized' }];
  }
}

export { buildIndentedCategoryList, buildCategoryTreeSorted, getCategoryDisplayLabel, CHILD_TO_PARENT, CATEGORY_PARENT_GROUPS };
